import { clientUuid } from './id';

export type UploadStatus = 'queued' | 'uploading' | 'uploaded' | 'failed';
export interface CheckUploadRow {
  id: string;
  eId: string;
  checkId: string;
  file: Blob;
  fileName: string;
  mimeType: string;
  fileSize: number;
  lastModified: number;
  status: UploadStatus;
  error?: string;
  updatedAt: string;
}

const DB_NAME = 'nova-check-uploads'; const STORE = 'uploads';
function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => { const db = request.result; if (!db.objectStoreNames.contains(STORE)) { const store = db.createObjectStore(STORE, { keyPath: 'id' }); store.createIndex('scope', ['eId', 'checkId']); } };
    request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
  });
}
function transaction<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore, done: (value: T) => void, fail: (reason?: unknown) => void) => void): Promise<T> {
  return database().then((db) => new Promise<T>((resolve, reject) => { const tx = db.transaction(STORE, mode); work(tx.objectStore(STORE), resolve, reject); tx.onerror = () => reject(tx.error); tx.oncomplete = () => db.close(); }));
}
export async function enqueueCheckFiles(eId: string, checkId: string, files: File[]): Promise<void> {
  await Promise.all(files.map((file) => transaction<void>('readwrite', (store, done, fail) => {
    // Safari/iOS nu garantează că un File citit ulterior din IndexedDB își păstrează
    // metadatele. Persistăm un Blob independent și metadatele separat.
    const blob = file.slice(0, file.size, file.type || 'application/octet-stream');
    const row: CheckUploadRow = {
      id: clientUuid(), eId, checkId, file: blob, fileName: file.name || 'foto.jpg',
      mimeType: file.type || blob.type || 'application/octet-stream', fileSize: file.size,
      lastModified: file.lastModified || Date.now(), status: 'queued', updatedAt: new Date().toISOString(),
    };
    const request = store.put(row); request.onsuccess = () => done(); request.onerror = () => fail(request.error);
  })));
}
export function listCheckUploads(eId: string, checkId: string): Promise<CheckUploadRow[]> {
  return transaction<CheckUploadRow[]>('readonly', (store, done, fail) => { const request = store.index('scope').getAll([eId, checkId]); request.onsuccess = () => done(request.result as CheckUploadRow[]); request.onerror = () => fail(request.error); });
}
export function updateCheckUpload(row: CheckUploadRow, status: UploadStatus, error = ''): Promise<void> {
  return transaction<void>('readwrite', (store, done, fail) => { const request = store.put({ ...row, status, error, updatedAt: new Date().toISOString() }); request.onsuccess = () => done(); request.onerror = () => fail(request.error); });
}
export function deleteCheckUpload(id: string): Promise<void> {
  return transaction<void>('readwrite', (store, done, fail) => { const request = store.delete(id); request.onsuccess = () => done(); request.onerror = () => fail(request.error); });
}

export async function materializeCheckUploadFile(row: CheckUploadRow): Promise<File> {
  const blob = row.file instanceof Blob ? row.file : new Blob([row.file as BlobPart]);
  // Materializarea ArrayBuffer-ului elimină referințele lazy către backing-store-ul
  // IndexedDB care pot produce multipart trunchiat în Safari/iOS.
  const bytes = await blob.arrayBuffer();
  return new File([bytes], row.fileName || 'foto.jpg', {
    type: row.mimeType || blob.type || 'application/octet-stream',
    lastModified: row.lastModified || Date.now(),
  });
}
